import React, { useState } from "react";
import styled from "styled-components";
import { Col, Row, Collapse } from "reactstrap";
import { RebelColors, RebelDangerButton, RebelIcon } from "../../../styles/rebel-experiences";
import { displayValue } from "../helper";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { calculateDateBasedAccommodation, hasDatePricing } from "../../../utils/date-pricing-utils";

/**
 * Property tab showing cost breakdown.
 * Uses date-based pricing for accommodation when available, otherwise falls back to basicViewPricing.
 * basicViewPricing is a map of category-id -> per-person price.
 */
const RebelBudgetPropertyTab = ({ villaId, villa, categories, config, tripLength, currencySymbol, onRemove, viewOnly }) => {
    const [showBreakdown, setShowBreakdown] = useState(false);

    if (!villa) {
        return <LoadingText>Loading property data...</LoadingText>;
    }

    const pricing = villa.basicViewPricing || {};
    const attendees = config.attendees || 1;
    const hasDates = config.startDate && config.endDate;
    const villaHasDatePricing = hasDatePricing(villa);

    // Compute date-based accommodation breakdown if available
    const accomBreakdown = (villaHasDatePricing && hasDates)
        ? calculateDateBasedAccommodation(config.startDate, config.endDate, villa)
        : null;

    // Get category name from id
    const getCategoryName = (catId) => {
        const cat = categories.find(c => c.id === catId || c._docId === catId);
        return cat?.name || cat?.title || catId;
    };

    // Check if a category is accommodation
    const isAccommodation = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging");
    };

    // Map category names to icons
    const getCategoryIcon = (catName) => {
        const name = (catName || "").toLowerCase();
        if (name.includes("accommodation") || name.includes("lodging")) return 'bed';
        if (name.includes("flight") || name.includes("air")) return 'plane';
        if (name.includes("activity") || name.includes("activities") || name.includes("excursion")) return 'running';
        if (name.includes("transport") || name.includes("ground") || name.includes("transfer")) return 'car';
        if (name.includes("meal") || name.includes("food") || name.includes("beverage") || name.includes("catering") ||
            name.includes("breakfast") || name.includes("lunch") || name.includes("dinner")) return 'utensils';
        if (name.includes("meeting") || name.includes("conference") || name.includes("av") || name.includes("setup")) return 'chalkboard-teacher';
        if (name.includes("misc") || name.includes("other") || name.includes("additional")) return 'plus-circle';
        if (name.includes("contingency") || name.includes("buffer")) return 'shield-alt';
        if (name.includes("coffee") || name.includes("break")) return 'coffee';
        return 'tag';
    };

    // Determine if a category should be multiplied by trip length
    const isDayBased = (catId, catName) => {
        const name = (catName || catId || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging") ||
               name.includes("meal") || name.includes("food") || name.includes("beverage") ||
               name.includes("breakfast") || name.includes("lunch") || name.includes("dinner");
    };

    // Build cost rows from basicViewPricing
    const costRows = Object.entries(pricing)
        .filter(([, price]) => parseFloat(price) > 0)
        .map(([catId, price]) => {
            const perPerson = parseFloat(price) || 0;
            const catName = getCategoryName(catId);
            const dayBased = isDayBased(catId, catName);
            const icon = getCategoryIcon(catName);

            // For accommodation with date pricing, override the cost
            if (isAccommodation(catName) && accomBreakdown) {
                const totalAccom = accomBreakdown.total; // total for the property (not per-person)
                const totalPerPerson = totalAccom;
                const totalForTrip = totalAccom; // accommodation is already total, not per-person
                return {
                    catId, catName, perPerson: accomBreakdown.subtotal / (accomBreakdown.nightCount || 1),
                    nights: accomBreakdown.nightCount, dayBased: true,
                    totalPerPerson: totalAccom, totalForTrip: totalAccom,
                    icon, isDateBased: true
                };
            }

            const nights = dayBased ? (tripLength || 1) : 1;
            const totalPerPerson = perPerson * nights;
            const totalForTrip = totalPerPerson * attendees;
            return { catId, catName, perPerson, nights, dayBased, totalPerPerson, totalForTrip, icon, isDateBased: false };
        });

    const subtotalPerPerson = costRows.reduce((sum, row) => {
        if (row.isDateBased) return sum + row.totalPerPerson;
        return sum + row.totalPerPerson;
    }, 0);
    const subtotalTrip = costRows.reduce((sum, row) => {
        if (row.isDateBased) return sum + row.totalForTrip;
        return sum + row.totalForTrip;
    }, 0);

    return (
        <div>
            {/* Property header */}
            <PropertyHeader>
                <PropertyName>{villa.villaName || villa.name || "Property"}</PropertyName>
                {villa.location && <PropertyLocation>{villa.location}</PropertyLocation>}
            </PropertyHeader>

            {/* Villa details */}
            {(villa.numberOfBedrooms || villa.distanceFromAirport || villa.nearestCity || villa.distanceToCity || villa.villaURL) && (
                <VillaDetailsContainer>
                    {villa.numberOfBedrooms > 0 && <VillaDetail><strong>Bedrooms:</strong> {villa.numberOfBedrooms}</VillaDetail>}
                    {villa.distanceFromAirport && <VillaDetail><strong>Distance from Airport:</strong> {villa.distanceFromAirport}</VillaDetail>}
                    {villa.nearestCity && <VillaDetail><strong>Nearest City:</strong> {villa.nearestCity}</VillaDetail>}
                    {villa.distanceToCity && <VillaDetail><strong>Distance to City:</strong> {villa.distanceToCity}</VillaDetail>}
                    {villa.villaURL && (
                        <ViewPropertyButton
                            href={villa.villaURL.startsWith('http') ? villa.villaURL : `https://${villa.villaURL}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Property
                        </ViewPropertyButton>
                    )}
                </VillaDetailsContainer>
            )}

            {/* Property notes */}
            {villa.notes && (
                <NotesContainer>
                    <NotesLabel>
                        <FontAwesomeIcon icon={['fas', 'sticky-note']} style={{ marginRight: 8 }} />
                        Property Notes
                    </NotesLabel>
                    <NotesText>{villa.notes}</NotesText>
                </NotesContainer>
            )}

            {/* Booking rule warnings */}
            {accomBreakdown && accomBreakdown.warnings.length > 0 && (
                <WarningsContainer>
                    {accomBreakdown.warnings.map((warning, i) => (
                        <WarningBadge key={i} $type={warning.type}>
                            <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ marginRight: 6 }} />
                            {warning.message}
                        </WarningBadge>
                    ))}
                </WarningsContainer>
            )}

            {/* Discount badge */}
            {accomBreakdown && accomBreakdown.discount.applied && (
                <DiscountBadge>
                    <FontAwesomeIcon icon={['fas', 'tag']} style={{ marginRight: 6 }} />
                    Discount applied: {accomBreakdown.discount.description} (-{currencySymbol}{displayValue(accomBreakdown.discount.amount)})
                </DiscountBadge>
            )}

            {/* Cost summary header */}
            <SummaryHeader>
                <SummaryItem>
                    <SummaryLabel>Per Attendee Cost</SummaryLabel>
                    <SummaryValue>{currencySymbol}{displayValue(subtotalPerPerson)}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                    <SummaryLabel>Total Cost ({attendees} attendees)</SummaryLabel>
                    <SummaryValue>{currencySymbol}{displayValue(subtotalTrip)}</SummaryValue>
                </SummaryItem>
            </SummaryHeader>

            {/* Cost breakdown table */}
            <TableTitle>Trip Breakdown</TableTitle>
            <CostTableContainer>
                {/* Header */}
                <HeaderRow>
                    <Col lg="1"></Col>
                    <Col>
                        <BoldLabel>Category</BoldLabel>
                    </Col>
                    <Col lg="2">
                        <BoldLabel>Per Attendee ({currencySymbol})</BoldLabel>
                    </Col>
                    <Col lg="1"></Col>
                    <Col lg="2">
                        <BoldLabel>Subtotal ({currencySymbol})</BoldLabel>
                    </Col>
                </HeaderRow>

                {/* Cost rows */}
                {costRows.map(row => (
                    <React.Fragment key={row.catId}>
                        <CostRow>
                            <IconCol lg="1">
                                <CategoryIcon icon={['fas', row.icon]} />
                            </IconCol>
                            <Col>
                                <CategoryName>{row.catName}</CategoryName>
                                <CostDescription>
                                    {row.isDateBased ? (
                                        <>
                                            {accomBreakdown.nightCount} night{accomBreakdown.nightCount !== 1 ? 's' : ''}
                                            {accomBreakdown.cleaningFee > 0 && ` + ${currencySymbol}${displayValue(accomBreakdown.cleaningFee)} cleaning`}
                                            <BreakdownToggle onClick={() => setShowBreakdown(!showBreakdown)}>
                                                {showBreakdown ? 'Hide' : 'View'} nightly rates
                                                <FontAwesomeIcon icon={['fas', showBreakdown ? 'chevron-up' : 'chevron-down']} style={{ marginLeft: 4, fontSize: 10 }} />
                                            </BreakdownToggle>
                                        </>
                                    ) : (
                                        <>
                                            {currencySymbol}{displayValue(row.perPerson)}/person
                                            {row.dayBased && tripLength > 0 && ` × ${row.nights} night${row.nights !== 1 ? 's' : ''}`}
                                        </>
                                    )}
                                </CostDescription>
                            </Col>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(row.totalPerPerson)}</CostAmount>
                            </Col>
                            <MultiplierCol lg="1">
                                {!row.isDateBased && <MultiplierText>× {attendees}</MultiplierText>}
                            </MultiplierCol>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(row.totalForTrip)}</CostAmount>
                            </Col>
                        </CostRow>

                        {/* Nightly breakdown for date-based accommodation */}
                        {row.isDateBased && (
                            <Collapse isOpen={showBreakdown}>
                                <NightlyBreakdownContainer>
                                    {accomBreakdown.nightlyBreakdown.map(night => (
                                        <NightlyRow key={night.date}>
                                            <NightDate>
                                                {new Date(night.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                            </NightDate>
                                            <NightRate $isOverride={night.isOverride}>
                                                {currencySymbol}{displayValue(night.rate)}
                                                {night.isOverride && <OverrideIndicator>custom</OverrideIndicator>}
                                            </NightRate>
                                            {night.isBlackout && <NightFlag $type="blackout">Blackout</NightFlag>}
                                            {night.isCallToBook && <NightFlag $type="callToBook">Call to Book</NightFlag>}
                                            {night.note && <NightNote>{night.note}</NightNote>}
                                        </NightlyRow>
                                    ))}
                                    {accomBreakdown.cleaningFee > 0 && (
                                        <NightlyRow>
                                            <NightDate>Cleaning Fee</NightDate>
                                            <NightRate>{currencySymbol}{displayValue(accomBreakdown.cleaningFee)}</NightRate>
                                        </NightlyRow>
                                    )}
                                    {accomBreakdown.discount.applied && (
                                        <NightlyRow>
                                            <NightDate style={{ color: '#10b981' }}>Discount</NightDate>
                                            <NightRate style={{ color: '#10b981' }}>-{currencySymbol}{displayValue(accomBreakdown.discount.amount)}</NightRate>
                                        </NightlyRow>
                                    )}
                                </NightlyBreakdownContainer>
                            </Collapse>
                        )}
                    </React.Fragment>
                ))}

                {costRows.length === 0 && (
                    <EmptyRow>
                        <Col>
                            <EmptyText>No pricing data available for this property.</EmptyText>
                        </Col>
                    </EmptyRow>
                )}

                {/* Total */}
                {costRows.length > 0 && (
                    <>
                        <SeparatorRow>
                            <Col><SeparatorLine /></Col>
                        </SeparatorRow>

                        <TotalRow>
                            <IconCol lg="1">
                                <TotalIcon icon={['fas', 'calculator']} />
                            </IconCol>
                            <Col>
                                <TotalLabel>Estimated Total</TotalLabel>
                            </Col>
                            <Col lg="2">
                                <TotalAmount>{currencySymbol}{displayValue(subtotalPerPerson)}</TotalAmount>
                            </Col>
                            <MultiplierCol lg="1">
                                <MultiplierText>× {attendees}</MultiplierText>
                            </MultiplierCol>
                            <Col lg="2">
                                <TotalAmount>{currencySymbol}{displayValue(subtotalTrip)}</TotalAmount>
                            </Col>
                        </TotalRow>
                    </>
                )}
            </CostTableContainer>

            {/* Remove property button */}
            {!viewOnly && (
                <RemoveRow>
                    <RebelDangerButton onClick={onRemove}>
                        Remove Property
                    </RebelDangerButton>
                </RemoveRow>
            )}
        </div>
    );
};

export default RebelBudgetPropertyTab;

// Calculate total cost for a villa (used by summary)
export const calculateVillaTotalPerPerson = (villa, categories, tripLength, config) => {
    if (!villa || !villa.basicViewPricing) return 0;

    const pricing = villa.basicViewPricing;

    const getCategoryName = (catId) => {
        const cat = categories.find(c => c.id === catId || c._docId === catId);
        return cat?.name || cat?.title || catId;
    };

    const isDayBased = (catId, catName) => {
        const name = (catName || catId || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging") ||
               name.includes("meal") || name.includes("food") || name.includes("beverage") ||
               name.includes("breakfast") || name.includes("lunch") || name.includes("dinner");
    };

    const isAccom = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging");
    };

    // Check for date-based accommodation pricing
    const hasDates = config && config.startDate && config.endDate;
    const villaHasDatePricing = hasDatePricing(villa);

    let total = 0;
    Object.entries(pricing).forEach(([catId, price]) => {
        const perPerson = parseFloat(price) || 0;
        if (perPerson > 0) {
            const catName = getCategoryName(catId);

            if (isAccom(catName) && villaHasDatePricing && hasDates) {
                // Use date-based accommodation calculation
                const breakdown = calculateDateBasedAccommodation(config.startDate, config.endDate, villa);
                total += breakdown.total;
            } else {
                const nights = isDayBased(catId, catName) ? (tripLength || 1) : 1;
                total += perPerson * nights;
            }
        }
    });

    return total;
};

const LoadingText = styled.p`
    color: ${RebelColors.textMuted};
    text-align: center;
    padding: 2rem;
`;

const PropertyHeader = styled.div`
    margin-bottom: 1rem;
`;

const PropertyName = styled.h3`
    color: ${RebelColors.gold};
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
`;

const PropertyLocation = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.9rem;
    margin-bottom: 0;
`;

const VillaDetailsContainer = styled.div`
    background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
    border: 1px solid rgba(253, 181, 6, 0.3);
    border-radius: 12px;
    padding: 20px;
    margin: 16px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2);
`;

const VillaDetail = styled.div`
    color: ${RebelColors.text};
    font-size: 14px;
    padding: 6px 14px;
    background: linear-gradient(135deg, rgba(253, 181, 6, 0.1) 0%, rgba(253, 181, 6, 0.05) 50%, transparent 100%);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 6px;

    strong {
        color: ${RebelColors.gold};
        margin-right: 4px;
    }
`;

const ViewPropertyButton = styled.a`
    display: inline-flex;
    align-items: center;
    padding: 8px 20px;
    background: linear-gradient(90deg, #fdb506 0%, #f59e0b 100%);
    border: 2px solid #fdb506;
    border-radius: 6px;
    color: #000000 !important;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover, &:visited, &:active, &:focus {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(253, 181, 6, 0.4);
        color: #000000 !important;
        text-decoration: none;
    }
`;

const NotesContainer = styled.div`
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(31, 41, 55, 0.6) 50%, rgba(55, 65, 81, 0.6) 100%);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 10px;
    padding: 16px 20px;
    margin: 12px 0;
`;

const NotesLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 600;
    font-size: 0.85rem;
    margin: 0 0 6px 0;
    display: flex;
    align-items: center;
`;

const NotesText = styled.p`
    color: ${RebelColors.text};
    font-size: 0.9rem;
    margin: 0;
    line-height: 1.5;
    white-space: pre-wrap;
`;

const WarningsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 12px 0;
`;

const WarningBadge = styled.div`
    background: ${props => {
        if (props.$type === 'blackout') return 'rgba(239, 68, 68, 0.15)';
        if (props.$type === 'callToBook') return 'rgba(251, 191, 36, 0.15)';
        return 'rgba(251, 191, 36, 0.1)';
    }};
    border: 1px solid ${props => {
        if (props.$type === 'blackout') return '#ef4444';
        if (props.$type === 'callToBook') return '#fbbf24';
        return '#fbbf24';
    }};
    color: ${props => {
        if (props.$type === 'blackout') return '#ef4444';
        if (props.$type === 'callToBook') return '#fbbf24';
        return '#fbbf24';
    }};
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
`;

const DiscountBadge = styled.div`
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid #10b981;
    color: #10b981;
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    margin: 12px 0;
    display: inline-flex;
    align-items: center;
`;

// ===== Summary header =====

const SummaryHeader = styled.div`
    display: flex;
    gap: 24px;
    margin: 16px 0;
    flex-wrap: wrap;
`;

const SummaryItem = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
`;

const SummaryLabel = styled.span`
    color: ${RebelColors.text};
    font-size: 0.95rem;
`;

const SummaryValue = styled.span`
    color: ${RebelColors.gold};
    font-size: 1.15rem;
    font-weight: 700;
`;

// ===== Trip Breakdown table =====

const TableTitle = styled.h4`
    color: ${RebelColors.gold};
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 8px;
`;

const CostTableContainer = styled.div`
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(31, 41, 55, 0.85) 50%, rgba(55, 65, 81, 0.85) 100%);
    border: 1px solid rgba(253, 181, 6, 0.3);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2);
`;

const HeaderRow = styled(Row)`
    border-bottom: 2px solid rgba(253, 181, 6, 0.4);
    padding-bottom: 8px;
    margin-bottom: 4px;
`;

const CostRow = styled(Row)`
    padding: 10px 0;
    border-bottom: 1px solid rgba(253, 181, 6, 0.15);
    display: flex;
    align-items: center;

    &:last-child {
        border-bottom: none;
    }
`;

const IconCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const CategoryIcon = styled(FontAwesomeIcon)`
    color: ${RebelColors.gold};
    font-size: 16px;
    width: 20px !important;
`;

const CategoryName = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
`;

const CostDescription = styled.p`
    color: ${RebelColors.textMuted};
    margin: 0;
    font-size: 0.8rem;
`;

const CostAmount = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    font-size: 0.9rem;
`;

const MultiplierCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MultiplierText = styled.p`
    color: ${RebelColors.textMuted};
    margin: 0;
    font-size: 0.85rem;
`;

const SeparatorRow = styled(Row)`
    padding: 0;
`;

const SeparatorLine = styled.div`
    background-color: ${RebelColors.gold};
    height: 2px;
    margin: 8px 0;
    border-radius: 1px;
`;

const TotalRow = styled(Row)`
    padding: 10px 0;
    display: flex;
    align-items: center;
`;

const TotalIcon = styled(FontAwesomeIcon)`
    color: ${RebelColors.gold};
    font-size: 16px;
    width: 20px !important;
`;

const TotalLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 700;
    margin: 0;
    font-size: 1rem;
`;

const TotalAmount = styled.p`
    color: ${RebelColors.gold};
    font-weight: 700;
    margin: 0;
    font-size: 1rem;
`;

const EmptyRow = styled(Row)`
    padding: 1rem 0;
`;

const EmptyText = styled.p`
    color: ${RebelColors.textMuted};
    text-align: center;
    font-style: italic;
    margin: 0;
`;

const BoldLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 600;
    margin-bottom: 0;
    font-size: 0.9rem;
`;

const RemoveRow = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
`;

const BreakdownToggle = styled.span`
    color: ${RebelColors.gold};
    cursor: pointer;
    margin-left: 8px;
    font-size: 0.75rem;
    text-decoration: underline;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
`;

const NightlyBreakdownContainer = styled.div`
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(253, 181, 6, 0.15);
    border-radius: 8px;
    padding: 10px 16px;
    margin: 4px 0 8px 40px;
`;

const NightlyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid rgba(253, 181, 6, 0.08);

    &:last-child {
        border-bottom: none;
    }
`;

const NightDate = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.8rem;
    min-width: 100px;
`;

const NightRate = styled.span`
    color: ${props => props.$isOverride ? RebelColors.gold : RebelColors.text};
    font-size: 0.8rem;
    font-weight: ${props => props.$isOverride ? '600' : '400'};
    display: flex;
    align-items: center;
    gap: 6px;
`;

const OverrideIndicator = styled.span`
    background: rgba(253, 181, 6, 0.2);
    color: ${RebelColors.gold};
    font-size: 0.65rem;
    padding: 1px 5px;
    border-radius: 3px;
    font-weight: 500;
`;

const NightFlag = styled.span`
    font-size: 0.7rem;
    padding: 2px 6px;
    border-radius: 3px;
    font-weight: 500;
    background: ${props => props.$type === 'blackout' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(251, 191, 36, 0.15)'};
    color: ${props => props.$type === 'blackout' ? '#ef4444' : '#fbbf24'};
    border: 1px solid ${props => props.$type === 'blackout' ? '#ef4444' : '#fbbf24'};
`;

const NightNote = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.7rem;
    font-style: italic;
`;
